import React, { useState, useEffect } from 'react';
import { Pressable } from 'react-native';
import { Text } from '@/components/gluestack/text';
import dayjs from '@/i18n/dayjs-config';
import { useTranslation } from 'react-i18next';

interface RelativeTimeProps {
  date: string | Date;
  className?: string;
  formatAbsolute?: string;
  useLocaleFormat?: boolean;
  textOnly?: boolean;
  short?: boolean;
  tiny?: boolean;
}

/**
 * Component to display a relative time that toggles to absolute time when clicked
 *
 * @param date - Date object or ISO date string
 * @param className - Optional styling for the Text component
 * @param formatAbsolute - Optional format for absolute time (dayjs format)
 * @param useLocaleFormat - Whether to use locale-based formatting (defaults to true)
 * @param textOnly - If true, renders only text without interactive functionality (better alignment)
 * @param short - If true, uses condensed format (e.g., "16m", "2h", "3d")
 * @param tiny - Like short, but floors sub-minute times to "1m" instead of a translated "now"
 */
export function RelativeTime({
  date,
  className = '',
  formatAbsolute = 'lll', // 'lll' is locale-based format (e.g., 'Feb 21, 2023 2:05 PM' in en-US)
  useLocaleFormat = true,
  textOnly = false,
  short = false,
  tiny = false,
}: RelativeTimeProps) {
  const [showAbsolute, setShowAbsolute] = useState(false);
  const [, setRefresh] = useState(0);
  const { i18n, t } = useTranslation();

  // Set up an interval to refresh the component every 30 seconds
  useEffect(() => {
    const intervalId = setInterval(() => {
      setRefresh((prev) => prev + 1);
    }, 30000);

    return () => clearInterval(intervalId);
  }, [i18n.language]); // Add i18n.language as dependency to refresh when language changes

  // Ensure we have a valid dayjs object regardless of input type
  const dateObj =
    typeof date === 'string' ? dayjs(date.endsWith('Z') ? date : date + 'Z') : dayjs(date);

  const diffSeconds = dayjs().diff(dateObj, 'second');
  const isJustNow = !showAbsolute && diffSeconds < 60;

  // Format the time based on state
  let formattedTime;
  if (showAbsolute) {
    if (useLocaleFormat) {
      // Use locale-based formatting
      formattedTime = dateObj.format(formatAbsolute);
    } else {
      // Use custom format provided
      formattedTime = dateObj.format(formatAbsolute);
    }
  } else if (isJustNow) {
    formattedTime = tiny
      ? '1m'
      : t(short ? 'common.relativeTime.now' : 'common.relativeTime.justNow');
  } else if (short || tiny) {
    formattedTime = formatShortRelativeTime(dateObj);
  } else {
    formattedTime = dateObj.fromNow();
  }

  if (textOnly) {
    return <Text className={className}>{formattedTime}</Text>;
  }

  return (
    <Pressable 
      onPress={() => setShowAbsolute(!showAbsolute)}
      className="inline-flex"
    >
      <Text className={className}>{formattedTime}</Text>
    </Pressable>
  );
}

function formatShortRelativeTime(dateObj: dayjs.Dayjs): string {
  const now = dayjs();

  const diffMinutes = Math.round(now.diff(dateObj, 'minute', true));
  if (diffMinutes < 60) return `${diffMinutes}m`;

  const diffHours = Math.round(now.diff(dateObj, 'hour', true));
  if (diffHours < 24) return `${diffHours}h`;

  const diffDays = Math.round(now.diff(dateObj, 'day', true));
  if (diffDays < 30) return `${diffDays}d`;

  const diffMonths = Math.round(now.diff(dateObj, 'month', true));
  if (diffMonths < 12) return `${diffMonths}mo`;

  const diffYears = Math.round(now.diff(dateObj, 'year', true));
  return `${diffYears}y`;
}

